import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  doc,
  onSnapshot,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { useVenue } from "./VenueProvider";
import { useAuth } from "./AuthProvider";
import { esSoloLectura } from "../utils/roles";
import { loadProveedorCatalogs } from "../utils/catalogs";
import { categoriasDe, esPlannerProveedor, daServicios } from "../utils/proveedores";
import { logBitacora, BITACORA_ACTIONS } from "../utils/bitacora";
import { totalCostos } from "../utils/costos";
import {
  proveedorDe,
} from "../utils/catalogo";
import PlannerComisionSection from "./directorio/PlannerComisionSection";
import ServiciosTab from "./proveedor/ServiciosTab";
import InfoTab from "./proveedor/InfoTab";
import PlannerResultados from "./proveedor/PlannerResultados";
import ContactosTab from "./proveedor/ContactosTab";
import DepositosTab from "./proveedor/DepositosTab";
import SaldoAFavorTab from "./proveedor/SaldoAFavorTab";
import NotasTab from "./proveedor/NotasTab";
import EventosTab from "./proveedor/EventosTab";
import ReviewsTab from "./proveedor/ReviewsTab";
import { ACCENT, fmtMoney, STATUS_OPTIONS } from "./proveedor/shared";
import { showAlert } from "../alertService";
import { Button } from "./button";
import { Heading } from "./heading";
import { Badge } from "./badge";
import {
  ChevronLeftIcon,
  StarIcon,
  BanknotesIcon,
  GlobeAltIcon,
  CalendarDaysIcon,
  ArchiveBoxIcon,
  ArchiveBoxArrowDownIcon,
} from "@heroicons/react/20/solid";


// La ficha ya no tiene pestañas (FRA-353): es una sola lectura vertical.
// Los ids se conservan como anclas para que las URLs viejas `?tab=...`
// (links guardados y los "Volver" de otras pantallas) caigan en su sección.
const SECCIONES = [
  { id: "resumen", label: "Información" },
  { id: "costo", label: "Servicios", labelPlanner: "Comisión" },
  { id: "depositos", label: "Depósitos" },
  { id: "saldo", label: "Saldo a favor" },
  { id: "eventos", label: "Eventos" },
  { id: "reviews", label: "Reseñas" },
  { id: "notas", label: "Notas" },
];

// Encabezado de capítulo. Todas las secciones lo usan para que la ficha se
// lea como capítulos y no como un muro: antes tres tenían una etiqueta
// diminuta, tres no tenían ninguna, y el espacio entre secciones era el
// mismo que entre las tarjetas de adentro.
const Seccion = ({ id, titulo, descripcion, children }) => (
  <section id={`seccion-${id}`} className="scroll-mt-20">
    <div className="mb-3">
      <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">{titulo}</h3>
      {descripcion && (
        <p className="mt-0.5 text-xs text-zinc-500">{descripcion}</p>
      )}
    </div>
    <div className="space-y-4">{children}</div>
  </section>
);





const ProveedorPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const backTarget = (() => {
    if (
      location.state?.from === "evento" &&
      location.state?.eventId
    ) {
      const eventTab = location.state.eventTab || "operacion";
      return {
        label: "Volver al evento",
        to: `/dashboard/eventos/${location.state.eventId}?tab=${encodeURIComponent(
          eventTab
        )}`,
      };
    }
    if (location.state?.from === "cuentas-depositos") {
      return {
        label: "Volver a depósitos en garantía",
        to: "/dashboard/cuentas/depositos",
      };
    }
    return { label: "Directorio", to: "/dashboard/directorio" };
  })();
  const { selectedVenueId, events, conceptos, costos } = useVenue();
  const { userInfo, user } = useAuth();
  // Inversionista (P-FRA-13): la ficha completa en lectura, sin archivar
  // ni editar nada. Cada sección apaga sus propios puntos de escritura.
  const soloLectura = esSoloLectura(userInfo);

  const [searchParams] = useSearchParams();
  const tab = useMemo(() => {
    const v = searchParams.get("tab");
    // `?tab=dinero` es de cuando la franja de dinero era pestaña propia; hoy
    // vive dentro de Servicios, así que ahí cae.
    if (v === "dinero") return "costo";
    return SECCIONES.some((t) => t.id === v) ? v : "resumen";
  }, [searchParams]);
  // `?tab=depositos` (y demás) ahora desplaza a la sección en vez de abrir
  // una pestaña. Se hace una vez que la ficha cargó.
  const irA = useCallback((id) => {
    const el = document.getElementById(`seccion-${id}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const [proveedor, setProveedor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [categorias, setCategorias] = useState([]);
  const [subcategorias, setSubcategorias] = useState([]);
  const [showDelete, setShowDelete] = useState(false);

  // Per-doc subscription
  useEffect(() => {
    if (!selectedVenueId || !id) return;
    const docRef = doc(db, "venues", selectedVenueId, "proveedores", id);
    const unsub = onSnapshot(
      docRef,
      (snap) => {
        if (!snap.exists()) {
          setNotFound(true);
          setProveedor(null);
        } else {
          setNotFound(false);
          setProveedor({ id: snap.id, ...snap.data() });
        }
        setLoading(false);
      },
      (err) => {
        console.error("Proveedor snapshot error:", err);
        setLoading(false);
      }
    );
    return unsub;
  }, [selectedVenueId, id]);

  useEffect(() => {
    let cancelled = false;
    loadProveedorCatalogs()
      .then((data) => {
        if (cancelled) return;
        // Mismo filtro que el diálogo de alta: una categoría desactivada no
        // se ofrece; si un proveedor ya la tiene, InfoTab la muestra con su
        // id como opción de respaldo.
        setCategorias(data.categorias.filter((c) => c.activo !== false));
        setSubcategorias(data.subcategorias.filter((s) => s.activo !== false));
      })
      .catch((e) => console.error(e));
    return () => {
      cancelled = true;
    };
  }, []);

  // Scroll-spy: qué sección se está leyendo, para marcarla en el índice. Sin
  // esto el índice nunca señala nada y se ve roto.
  const [seccionActiva, setSeccionActiva] = useState("resumen");
  useEffect(() => {
    if (!proveedor) return;
    const nodos = SECCIONES.map((t) => document.getElementById(`seccion-${t.id}`)).filter(Boolean);
    if (!nodos.length) return;
    const obs = new IntersectionObserver(
      (entradas) => {
        const visibles = entradas
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visibles.length) {
          setSeccionActiva(visibles[0].target.id.replace("seccion-", ""));
        }
      },
      // El margen superior descuenta el índice sticky; el inferior evita que
      // una sección apenas asomada por abajo se robe el activo.
      { rootMargin: "-72px 0px -55% 0px", threshold: 0 }
    );
    nodos.forEach((n) => obs.observe(n));
    return () => obs.disconnect();
  }, [proveedor]);

  const scrolledRef = React.useRef(null);
  useEffect(() => {
    if (!proveedor || tab === "resumen" || scrolledRef.current === tab) return;
    scrolledRef.current = tab;
    const t = setTimeout(() => irA(tab), 120);
    return () => clearTimeout(t);
  }, [proveedor, tab, irA]);

  // Texto de la tarjeta de identidad: todas las categorías del proveedor,
  // principal primero ("Catering · Banquetes, Decoración").
  const categoriasTexto = useMemo(() => {
    if (!proveedor) return null;
    const partes = categoriasDe(proveedor).map((e) => {
      const cat = categorias.find((c) => c.id === e.categoria)?.nombre || e.categoria;
      const sub = e.subcategoria
        ? subcategorias.find((s) => s.id === e.subcategoria)?.nombre
        : null;
      return sub ? `${cat} · ${sub}` : cat;
    });
    return partes.length ? partes.join(", ") : null;
  }, [proveedor, categorias, subcategorias]);

  // Servicios del catálogo que da este proveedor (FRA-349): ahí vive su
  // costo, ya no en el proveedor.
  const serviciosDelProveedor = useMemo(
    () =>
      (Array.isArray(conceptos) ? conceptos : [])
        .filter((c) => !c.archivedAt && proveedorDe(c) === id)
        .sort((a, b) => (a.nombre || "").localeCompare(b.nombre || "")),
    [conceptos, id]
  );

  const updateField = async (patch, bitacoraDetails = {}) => {
    if (!selectedVenueId || !id) return;
    const docRef = doc(db, "venues", selectedVenueId, "proveedores", id);
    try {
      await updateDoc(docRef, {
        ...patch,
        updatedAt: serverTimestamp(),
        updatedBy: user?.uid || null,
      });
      logBitacora(
        selectedVenueId,
        {
          action: BITACORA_ACTIONS.PROVEEDOR_EDIT,
          entity: "proveedor",
          entityId: id,
          entityName: proveedor?.nombre || "",
          details: bitacoraDetails,
        },
        userInfo,
        user
      );
    } catch (e) {
      console.error(e);
      showAlert("Error al guardar: " + e.message, "error");
    }
  };

  const handleArchive = async () => {
    if (!selectedVenueId || !id) return;
    try {
      await updateDoc(
        doc(db, "venues", selectedVenueId, "proveedores", id),
        {
          archivedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          updatedBy: user?.uid || null,
        }
      );
      logBitacora(
        selectedVenueId,
        {
          action: BITACORA_ACTIONS.PROVEEDOR_ARCHIVE,
          entity: "proveedor",
          entityId: id,
          entityName: proveedor?.nombre || "",
          details: {},
        },
        userInfo,
        user
      );
      showAlert("Proveedor archivado", "success");
      setShowDelete(false);
    } catch (e) {
      console.error(e);
      showAlert("Error al archivar: " + e.message, "error");
    }
  };

  const handleUnarchive = async () => {
    if (!selectedVenueId || !id) return;
    try {
      await updateDoc(
        doc(db, "venues", selectedVenueId, "proveedores", id),
        {
          archivedAt: null,
          updatedAt: serverTimestamp(),
          updatedBy: user?.uid || null,
        }
      );
      logBitacora(
        selectedVenueId,
        {
          action: BITACORA_ACTIONS.PROVEEDOR_UNARCHIVE,
          entity: "proveedor",
          entityId: id,
          entityName: proveedor?.nombre || "",
          details: {},
        },
        userInfo,
        user
      );
      showAlert("Proveedor desarchivado", "success");
    } catch (e) {
      console.error(e);
      showAlert("Error al desarchivar: " + e.message, "error");
    }
  };

  // Dinero ejercido con este proveedor: vive arriba, en la franja única.
  const costosSuyos = useMemo(
    () => (Array.isArray(costos) ? costos : []).filter((c) => c.proveedorId === id),
    [costos, id]
  );
  const pagado = totalCostos(costosSuyos, { estado: "pagado" });
  const porPagar = totalCostos(costosSuyos, { estado: "por_pagar" });

  if (loading) {
    return <div className="p-8 text-center text-zinc-400">Cargando...</div>;
  }
  if (notFound || !proveedor) {
    return (
      <div className="p-8 text-center">
        <div className="text-zinc-500 mb-4">Proveedor no encontrado.</div>
        <Button outline onClick={() => navigate("/dashboard/directorio")}>
          <ChevronLeftIcon className="h-4 w-4 mr-1" /> Volver
        </Button>
      </div>
    );
  }

  const status =
    STATUS_OPTIONS.find((s) => s.value === (proveedor.status || "activo")) ||
    STATUS_OPTIONS[0];

  // Identity card metrics
  const initial = (proveedor.nombre || "?").trim().charAt(0).toUpperCase();
  const eventCount = (events || []).reduce((n, e) => {
    if (!Array.isArray(e.proveedoresAsignados)) return n;
    return n + (e.proveedoresAsignados.some((a) => a?.proveedorId === id) ? 1 : 0);
  }, 0);
  const reviewsCount = proveedor.ratingCount || 0;
  const esPlanner = esPlannerProveedor(proveedor);
  const conServicios = daServicios(proveedor);
  const statusBadgeColor =
    status.value === "activo" ? "emerald" : status.value === "bloqueado" ? "red" : "zinc";

  return (
    <div className="max-w-5xl mx-auto">
      {/* Back link */}
      <button
        onClick={() => navigate(backTarget.to)}
        className="inline-flex items-center text-sm text-zinc-500 hover:text-zinc-700 mb-3"
      >
        <ChevronLeftIcon className="h-4 w-4 mr-1" /> {backTarget.label}
      </button>

      {/* Identity card */}
      <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 shadow-sm overflow-hidden mb-4">
        <div className="p-5 sm:p-6">
          <div className="flex items-start gap-4 flex-wrap">
            {/* Avatar initial */}
            <div
              className="shrink-0 h-14 w-14 rounded-xl flex items-center justify-center text-2xl font-bold text-white"
              style={{ backgroundColor: ACCENT }}
              aria-hidden="true"
            >
              {initial}
            </div>
            {/* Identity */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <Heading level={2} className="break-words">
                  {proveedor.nombre}
                </Heading>
                {proveedor.isPlanner && <Badge color="indigo">Planner</Badge>}
                <Badge color={statusBadgeColor}>{status.label}</Badge>
                {proveedor.archivedAt && (
                  <Badge color="zinc">Archivado</Badge>
                )}
              </div>
              {categoriasTexto && (
                <div className="mt-1 text-sm text-zinc-500">{categoriasTexto}</div>
              )}
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                {proveedor.website && (
                  <a
                    href={/^https?:\/\//i.test(proveedor.website) ? proveedor.website : `https://${proveedor.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium hover:bg-blue-100 transition"
                  >
                    <GlobeAltIcon className="h-3.5 w-3.5" />
                    Sitio web
                  </a>
                )}
                {proveedor.instagram && (
                  <a
                    href={`https://instagram.com/${proveedor.instagram}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-pink-50 text-pink-700 text-xs font-medium hover:bg-pink-100 transition"
                  >
                    @{proveedor.instagram}
                  </a>
                )}
              </div>
            </div>
            {/* Actions */}
            {soloLectura ? null : proveedor.archivedAt ? (
              <button
                type="button"
                onClick={handleUnarchive}
                className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-zinc-700 bg-zinc-100 hover:bg-zinc-200 transition"
                title="Desarchivar proveedor"
              >
                <ArchiveBoxArrowDownIcon className="h-4 w-4 rotate-180" />
                Desarchivar
              </button>
            ) : showDelete ? (
              <div className="shrink-0 inline-flex items-center gap-1.5 rounded-lg bg-zinc-50 border border-zinc-200 p-1">
                <span className="px-2 text-xs text-zinc-600">¿Archivar?</span>
                <button
                  type="button"
                  onClick={handleArchive}
                  className="px-2.5 py-1 rounded-md text-xs font-semibold text-white shadow-sm transition hover:opacity-90"
                  style={{ backgroundColor: ACCENT }}
                >
                  Sí, archivar
                </button>
                <button
                  type="button"
                  onClick={() => setShowDelete(false)}
                  className="px-2 py-1 rounded-md text-xs text-zinc-500 hover:text-zinc-700 transition"
                >
                  Cancelar
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowDelete(true)}
                className="shrink-0 p-2 rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition"
                title="Archivar proveedor"
              >
                <ArchiveBoxIcon className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
        {/* Una sola franja (antes eran dos: esta y otra dentro de Servicios,
            con "Eventos" repetido y números distintos). Lo que se pregunta de
            un proveedor: cuánto ha trabajado, cuánto le has pagado, cuánto le
            debes y qué tal salió. */}
        <div className="border-t border-zinc-100 dark:border-zinc-700/40 grid grid-cols-2 sm:grid-cols-4 divide-x divide-y sm:divide-y-0 divide-zinc-100 dark:divide-zinc-700/40 text-center">
          <div className="px-3 py-3">
            <div className="flex items-center justify-center gap-1.5 text-zinc-400">
              <CalendarDaysIcon className="h-3.5 w-3.5" />
              <span className="text-xs uppercase tracking-wide font-semibold">Eventos</span>
            </div>
            <div className="mt-0.5 text-sm font-bold text-zinc-900 dark:text-white">
              {eventCount || <span className="text-zinc-300">0</span>}
            </div>
          </div>
          <div className="px-3 py-3">
            <div className="flex items-center justify-center gap-1.5 text-zinc-400">
              <BanknotesIcon className="h-3.5 w-3.5" />
              <span className="text-xs uppercase tracking-wide font-semibold">Pagado</span>
            </div>
            <div className="mt-0.5 text-sm font-bold text-emerald-600">
              {pagado > 0 ? fmtMoney(pagado) : <span className="text-zinc-300">—</span>}
            </div>
          </div>
          <div className="px-3 py-3">
            <div className="flex items-center justify-center gap-1.5 text-zinc-400">
              <BanknotesIcon className="h-3.5 w-3.5" />
              <span className="text-xs uppercase tracking-wide font-semibold">Le debes</span>
            </div>
            <div className="mt-0.5 text-sm font-bold text-amber-600">
              {porPagar > 0 ? fmtMoney(porPagar) : <span className="text-zinc-300">—</span>}
            </div>
          </div>
          <div className="px-3 py-3">
            <div className="flex items-center justify-center gap-1.5 text-zinc-400">
              <StarIcon className="h-3.5 w-3.5" />
              <span className="text-xs uppercase tracking-wide font-semibold">Rating</span>
            </div>
            <div className="mt-0.5 text-sm font-bold text-zinc-900 dark:text-white">
              {reviewsCount > 0 ? (
                <>
                  {Number(proveedor.ratingPromedio).toFixed(1)}
                  <span className="text-xs font-normal text-zinc-400 ml-1">
                    ({reviewsCount})
                  </span>
                </>
              ) : (
                <span className="text-zinc-300">—</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Índice de secciones. Pills y NO una barra de pestañas a propósito:
          la ficha es una sola lectura vertical y esto solo desplaza. Con el
          look de tabs (borde inferior, sin estado activo) parecía que iba a
          cambiar de vista y en vez de eso brincaba la página. Sticky, porque
          un índice que desaparece al primer salto no sirve de nada. */}
      <div
        className="sticky top-0 z-10 -mx-1 mb-6 px-2 py-2 rounded-xl border border-[#1b4d2e26] dark:border-[#1b4d2e66] backdrop-blur-sm"
        style={{ backgroundColor: "#1b4d2e0f" }}
      >
        <nav className="flex flex-nowrap items-center gap-1.5 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {SECCIONES.map((t) => {
            const activa = seccionActiva === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => irA(t.id)}
                aria-current={activa ? "true" : undefined}
                className={`shrink-0 rounded-full px-3 py-1 text-xs font-semibold transition ${
                  activa
                    ? "text-white shadow-sm"
                    : "text-[#1b4d2e] hover:bg-[#1b4d2e1a] dark:text-emerald-200 dark:hover:bg-[#1b4d2e66]"
                }`}
                style={activa ? { backgroundColor: ACCENT } : undefined}
              >
                {esPlanner && t.labelPlanner ? t.labelPlanner : t.label}
              </button>
            );
          })}
        </nav>
      </div>

      <div className="space-y-10">
        <Seccion
          id="resumen"
          titulo="Información"
          descripcion="Quién es, cómo contactarlo y quién responde de su lado."
        >
          <InfoTab
            proveedor={proveedor}
            soloLectura={soloLectura}
            categorias={categorias}
            allSubcategorias={subcategorias}
            updateField={updateField}
            serviciosCount={serviciosDelProveedor.length}
            onVerServicios={() => irA("costo")}
          />
          <ContactosTab
            proveedor={proveedor}
            soloLectura={soloLectura}
            venueId={selectedVenueId}
            userInfo={userInfo}
            user={user}
          />
        </Seccion>

        <Seccion
          id="costo"
          titulo={esPlanner && conServicios ? "Comisión y servicios" : esPlanner ? "Comisión" : "Servicios"}
          descripcion={
            esPlanner && conServicios
              ? "Lo que se acordó pagarle por evento como planner, y lo que además te da como proveedor."
              : esPlanner
              ? "Lo que se acordó pagarle por evento y las comisiones ya registradas."
              : "Lo que te da y a qué tarifa, más lo que llevas gastado con él."
          }
        >
          {/* Un planner que además da servicios tiene las dos cosas: su
              acuerdo de comisión y su catálogo. */}
          {esPlanner && <PlannerComisionSection proveedor={proveedor} soloLectura={soloLectura} />}
          {conServicios && (
            <div className={esPlanner ? "mt-4" : undefined}>
              <ServiciosTab
                proveedor={proveedor}
                soloLectura={soloLectura}
                servicios={serviciosDelProveedor}
                navigate={navigate}
              />
            </div>
          )}
        </Seccion>

        <Seccion
          id="depositos"
          titulo="Depósitos en garantía"
          descripcion="Dinero suyo que tienes en custodia: está en tu cuenta pero no es tuyo."
        >
          <DepositosTab
            proveedor={proveedor}
            soloLectura={soloLectura}
            venueId={selectedVenueId}
            events={events}
            navigate={navigate}
            userInfo={userInfo}
            user={user}
          />
        </Seccion>

        <Seccion
          id="saldo"
          titulo="Saldo a favor"
          descripcion="Lo que ya le pagaste por eventos que se cancelaron: te lo debe, o se aplica a otro evento."
        >
          <SaldoAFavorTab
            proveedor={proveedor}
            soloLectura={soloLectura}
            venueId={selectedVenueId}
            costos={costosSuyos}
            events={events}
            navigate={navigate}
            userInfo={userInfo}
            user={user}
          />
        </Seccion>

        <Seccion
          id="eventos"
          titulo="Eventos"
          descripcion={
            esPlanner
              ? "Qué eventos ha traído y cómo salieron: valor, cobrado, comisión, costos y utilidad."
              : "En qué eventos ha trabajado y en cuáles está comprometido."
          }
        >
          {esPlanner && (
            <div className="mb-4">
              <PlannerResultados proveedor={proveedor} navigate={navigate} />
            </div>
          )}
          <EventosTab
            proveedor={proveedor}
            venueId={selectedVenueId}
            events={events}
            navigate={navigate}
            currentTab="eventos"
          />
        </Seccion>

        <Seccion
          id="reviews"
          titulo="Reseñas"
          descripcion="Cómo le fue según tu equipo, para decidir si lo vuelves a contratar."
        >
          <ReviewsTab
            proveedor={proveedor}
            soloLectura={soloLectura}
            venueId={selectedVenueId}
            userInfo={userInfo}
            user={user}
          />
        </Seccion>

        <Seccion
          id="notas"
          titulo="Notas"
          descripcion="La conversación interna sobre este proveedor."
        >
          <NotasTab
            proveedor={proveedor}
            soloLectura={soloLectura}
            venueId={selectedVenueId}
            userInfo={userInfo}
            user={user}
          />
        </Seccion>
      </div>
    </div>
  );
};

export default ProveedorPage;
